import { Button, Grid } from '@mui/material';
import * as Yup from 'yup';
import { FormInput } from '../../../../components/hook-form/form-input.component';
import { ReactHookForm } from '../../../../components/hook-form/react-hook-form.component';
import { useSaveData } from '../../../../hooks/useSaveData';
import { FileUploaded } from '../../../../models/fileUpload';
import { fileUploadApiKeyStore } from '../../../../api/file-upload-key-store';
import { useReactHookForm } from '../../../../hooks/useReactHookForm';
import { LoadingIndicator } from '../../../../components/loading-indicator.component';
import { useDropzone } from 'react-dropzone';
import { useCallback, useEffect, useState } from 'react';
import { GuardedComponent } from '../../../../components/guarded-component';
import { UserPermissionName } from '../../../../models/user-permission-name';
import { useAuthorize } from '../../../../hooks/use-authorize';
import { FormSelectDynamic } from '../../../../components/hook-form/form-select.dynamic.component';
import { useLoadData } from '../../../../hooks/useLoadData';
import { ApiResponse } from '../../../../models/api-response';
import { FormSelect } from '../../../query-builder/hooks-form-querybuilder/form-select.component';
import { unitsApiKeyStore } from '../../../../api/units-api-key-store';

const styling = {
  dropZone: {
    padding: '50px',
    border: '0.1em dashed #99A3A4',
    backgroundColor: '#F2F3F4',
    display: 'flex',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '4px',
  },
  iconButtonStyle: {
    padding: '15px',
  },
  iconStyle: { width: '40px', height: '40px' },
};

interface DocumentRequestArgs {
  unitID?: number;
  projectID?: number;
  tempEntityID?: number;
}

interface UploadFileComponent {
  onClose: () => void;
  fileData?: any;
  onSaveComplete: (data: any) => void;
  documentRequestArgs: DocumentRequestArgs;
}

const fileValidationSchema = Yup.object().shape({
  fileTypeID: Yup.string().required('File type is required'),
});

export const UploadFileComponent = ({ fileData, onClose, onSaveComplete, documentRequestArgs }: UploadFileComponent) => {
  

  const [files, setFiles] = useState<any>();
  const [fileItemData, setFileItemData] = useState<any>();
  const form = useReactHookForm({
    defaultValues: {
      ...fileData, ...documentRequestArgs,
      fileTypeID: fileData?.fileTypeID === 0 ? 'n/a' : fileData?.fileTypeID
    }, yupSchema: fileValidationSchema
  });
  const { isLoading, mutateAsync } = useSaveData<FileUploaded>(
    fileData ? fileUploadApiKeyStore.updateUploadFile() : fileUploadApiKeyStore.uploadFile());

  const { data: fileTypeData, isLoading: isFileTypeLoading } = useLoadData<ApiResponse<any>>(
    unitsApiKeyStore.getFileTypes(documentRequestArgs?.unitID)
  );

  useEffect(() => {
    if (fileTypeData) {
      const fileData = fileTypeData?.data;
      const formattedFileData = [
        { value: 'n/a', label: 'N/A' },
        ...(fileData?.map((item: any) => ({
          value: item.fileTypeID,
          label: item.fileTypeName
        })) || [])
      ];
      setFileItemData(formattedFileData)
    }
  }, [fileTypeData])

  const handleDrop = useCallback((acceptedFiles: any) => {
    setFiles(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
  });

  const submitHanlder = async (value: any) => {
    if (files || fileData) {
      const formData = new FormData();
      formData.append('fileID', value?.fileID);
      formData.append('UploadedFile', files ?? '');
      formData.append('FileName', files ? files?.name : value?.fileName);
      formData.append('FileType', files ? files?.type : value?.fileType);
      formData.append('Description', value.description ?? '');
      formData.append('FileSection', 'project');
      formData.append('UnitID', value.unitID);      
      formData.append('FileTypeID', value.fileTypeID == 'n/a' ? 0 : value.fileTypeID ?? 0);
      const data = await mutateAsync({ data: formData });
      onSaveComplete(data.data);
      form.reset({ fileUpload: [], description: '' });
    }
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <ReactHookForm formName="UNIT_FILE_FORM" onSubmit={submitHanlder} formContextProps={form}>
      <LoadingIndicator isLoading={isLoading} />
      <Grid container spacing={2}>
        <Grid item lg={7} sm={7} xl={7} xs={7}>
          <div style={styling.dropZone} {...getRootProps()}>
            <input {...getInputProps()} />
            {isDragActive ? (
              <p>Drop the files here ...</p>
            ) : (
              <div>
                {files ? (
                  <p>{`${files.name} are selected to upload`}</p>
                ) : (
                  fileData ?
                    <p>{`${fileData?.fileName} are selected to upload`}</p> :
                    <p>Drag and drop a file here, or click to select a file</p>
                )}
              </div>
            )}
          </div>
        </Grid>
        <Grid item lg={5} sm={5} xl={5} xs={5}>
          <FormInput label="Description" name="description" multiline={true} size={4.5} autoFocus />
        </Grid>
        
        <Grid item lg={4} sm={4} xl={4} xs={4}>
          <LoadingIndicator isLoading={isFileTypeLoading} />
          <FormSelect
            label="File Type"
            name="fileTypeID"
            options={fileItemData}
          />
        </Grid>
        
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <div className="spaceBetween">
            <Button variant="outlined" style={{ width: '130px' }} onClick={onClose}>
              Cancel
            </Button>
            <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
              <Button type={'button'} variant="contained" style={{ width: '130px' }}
                disabled={isReadOnlyPermission}
                onClick={() => form.handleSubmit(submitHanlder)()}>
                Save
              </Button>
            </GuardedComponent>
          </div>
        </Grid>
      </Grid>
    </ReactHookForm>
  );
};
